/**
 * Compute how many elements are considered answered on a step.
 *
 * Rules:
 * - Skipped items are counted as answered.
 * - The result is based on the index of the last answered element on the step (within the set of answerable elements).
 *
 * @param answered - Set of IDs for answered elements.
 * @param answerable - Set of IDs for answerable items and fields on the step.
 * @returns The number of answered elements, or 0 if none are answered.
 */
export function getNumberOfAnsweredElements(
  answered: Set<string>,
  answerable: Set<string>,
): number {
  let maxIndex: number = -1;
  let i: number = 0;

  for (const id of answerable) {
    if (answered.has(id)) {
      maxIndex = i;
    }
    i++
  }
  return maxIndex + 1;
}

/**
 * Computes the status label shown for a progress bar step.
 *
 * @param completedLabel - Translation for "Completed".
 * @param isCompleted - Whether the step is completed.
 * @param percentComplete - The completion percentage for the step.
 * @param timeEstimate - Optional time estimate shown when progress is 0%.
 * @returns The status string to display.
 */
export function getProgressStepStatus(
  completedLabel: string,
  isCompleted: boolean,
  percentComplete: number,
  timeEstimate: string | undefined,
): string {
  if (isCompleted) return completedLabel;
  if (percentComplete === 0) return timeEstimate ?? '';
  return `${Math.round(percentComplete)}%`;
}

/**
 * Computes the width percentage for a progress bar step.
 *
 * @param explicitPercentWidth - Optional explicit percent width configured on the step.
 * @param numberOfStepsInSurvey - Total number of steps in the survey.
 * @returns The percent width for the step, clamped to a maximum of 100.
 */
export function getProgressStepPercentWidth(
  explicitPercentWidth: number | undefined,
  numberOfStepsInSurvey: number | undefined,
): number {
  const fallback: number = 100 / Math.max(1, numberOfStepsInSurvey ?? 0);
  const width: number = explicitPercentWidth ?? fallback;
  return Math.min(100, width);
}